import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, ExternalLink, Trash2, Pause, Play, Search, Globe, Mail, Clock, Crown, AlertTriangle, Award, MailCheck, CalendarPlus, Link2,
  Edit3, KeyRound, CalendarX, Download, ShieldAlert, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { publicUrl } from '@/lib/publicUrl';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Master "Sites" page — lists every website-builder signup (trial + paid),
 * with owner email, business name, plan, trial days remaining, and moderation
 * actions (Open public site, Suspend, Delete).
 */
export default function MasterSites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');  // all | trial | paid | published | suspended
  const [busy, setBusy] = useState(null);
  const [requestDialog, setRequestDialog] = useState(null); // { site, message }
  const [scheduleDialog, setScheduleDialog] = useState(null); // { site, days, reason }
  const navigate = useNavigate();
  const token = localStorage.getItem('masterToken') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/master/sites`, { headers });
      setSites(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch sites');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredBySearch = q ? sites.filter(s => {
    const blob = [s.owner_name, s.owner_email, s.business_name, s.name, s.subdomain, s.custom_domain].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(q.toLowerCase());
  }) : sites;
  const filtered = filteredBySearch.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'trial') return (s.subscription_status || 'trial') === 'trial';
    if (filter === 'paid') return ['active', 'paid'].includes(s.subscription_status);
    if (filter === 'published') return s.published;
    if (filter === 'suspended') return s.suspended;
    return true;
  });

  const handleDelete = async (s) => {
    // Iter 175i — "Delete" now opens the Schedule Deletion dialog (with
    // configurable 1–30 day notice). Use the master_collab "Schedule
    // Deletion" flow instead of immediately wiping the site.
    setScheduleDialog({ site: s, days: 7, reason: '' });
  };

  const handleHardDelete = async (s) => {
    if (!window.confirm(`Permanently delete "${s.business_name || s.name || s.site_id}" RIGHT NOW (no notice)? This bypasses the 7-day grace period. Cannot be undone.`)) return;
    setBusy(s.site_id);
    try {
      await axios.delete(`${API}/master/sites/${s.site_id}`, { headers });
      toast.success('Site deleted');
      setSites(prev => prev.filter(x => x.site_id !== s.site_id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
    finally { setBusy(null); }
  };

  // Iter 175i — Master collab handlers
  const handleEditSite = async (s) => {
    setBusy(s.site_id);
    try {
      const r = await axios.post(`${API}/master/sites/${s.site_id}/impersonate`, {}, { headers });
      // Store the impersonation token + meta in sessionStorage so the
      // builder layout can pick it up and the master can return cleanly.
      sessionStorage.setItem('masterImpersonation', JSON.stringify({
        token: r.data.token,
        site_id: r.data.site_id,
        site_name: r.data.site_name,
        user: r.data.user,
        started_at: Date.now(),
      }));
      navigate(`/master/sites/${s.site_id}/builder`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to open editor'); }
    finally { setBusy(null); }
  };

  const submitRequestAccess = async () => {
    const { site, message } = requestDialog || {};
    if (!site) return;
    setBusy(site.site_id);
    try {
      await axios.post(`${API}/master/sites/${site.site_id}/request-access`, { message }, { headers });
      toast.success(`Request sent. ${site.owner_email} will see it in their Approvals inbox.`);
      setRequestDialog(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send request'); }
    finally { setBusy(null); }
  };

  const submitScheduleDeletion = async () => {
    const { site, days, reason } = scheduleDialog || {};
    if (!site) return;
    setBusy(site.site_id);
    try {
      await axios.post(`${API}/master/sites/${site.site_id}/schedule-deletion`,
        { days: Number(days) || 7, reason }, { headers });
      toast.success(`Scheduled for deletion in ${days} day(s). Owner has been emailed.`);
      setScheduleDialog(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to schedule'); }
    finally { setBusy(null); }
  };

  const handleCancelDeletion = async (s) => {
    if (!window.confirm(`Cancel the scheduled deletion of "${s.business_name || s.name}"? The site will be reactivated.`)) return;
    setBusy(s.site_id);
    try {
      await axios.post(`${API}/master/sites/${s.site_id}/cancel-deletion`, {}, { headers });
      toast.success('Deletion cancelled. Site reactivated.');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to cancel'); }
    finally { setBusy(null); }
  };

  const handleExport = async (s, fmt) => {
    setBusy(s.site_id);
    try {
      const r = await axios.get(`${API}/master/sites/${s.site_id}/export?format=${fmt}`, {
        headers, responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: fmt === 'zip' ? 'application/zip' : 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entreprenatives_${s.subdomain || s.site_id}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${s.business_name || s.name} as ${fmt.toUpperCase()}`);
    } catch (e) { toast.error('Export failed'); }
    finally { setBusy(null); }
  };

  const handleToggleSuspend = async (s) => {
    const action = s.suspended ? 'unsuspend' : 'suspend';
    setBusy(s.site_id);
    try {
      await axios.post(`${API}/master/sites/${s.site_id}/${action}`, {}, { headers });
      toast.success(`Site ${action}ed`);
      setSites(prev => prev.map(x => x.site_id === s.site_id ? { ...x, suspended: !s.suspended, published: s.suspended ? x.published : false } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const handleGrantFounding = async (s) => {
    const target = s.vendor_id || s.user_id;
    if (!target) return toast.error('No vendor or user id on this site');
    if (!window.confirm(`Promote "${s.business_name || s.owner_email}" to Founding Vendor? (Use this when the Stripe webhook missed but they paid.)`)) return;
    setBusy(s.site_id);
    try {
      const r = await axios.post(`${API}/founding-vendors/master/grant/${target}`, {}, { headers });
      const num = r.data?.founding_number;
      toast.success(num ? `Granted Founding Vendor #${num}` : (r.data?.message || 'Granted'));
      setSites(prev => prev.map(x => x.site_id === s.site_id
        ? { ...x, is_founding_vendor: true, founding_vendor_number: num || x.founding_vendor_number, membership_level: 'elite' }
        : x));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to grant'); }
    finally { setBusy(null); }
  };

  const handleRevokeFounding = async (s) => {
    const target = s.vendor_id || s.user_id;
    if (!target) return;
    if (!window.confirm(`Revoke Founding Vendor #${s.founding_vendor_number} from "${s.business_name || s.owner_email}"?`)) return;
    setBusy(s.site_id);
    try {
      await axios.post(`${API}/founding-vendors/master/revoke/${target}`, {}, { headers });
      toast.success('Founding Vendor revoked');
      setSites(prev => prev.map(x => x.site_id === s.site_id
        ? { ...x, is_founding_vendor: false, founding_vendor_number: null, membership_level: 'basic' }
        : x));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(null); }
  };

  const handleExtendTrial = async (s) => {
    const days = window.prompt(`Extend trial for "${s.business_name || s.owner_email}" by how many days?`, '14');
    if (!days) return;
    const n = parseInt(days, 10);
    if (!Number.isFinite(n) || n < 1) return toast.error('Enter a positive number of days');
    setBusy(s.site_id);
    try {
      const r = await axios.post(`${API}/master/sites/${s.site_id}/extend-trial`, { days: n }, { headers });
      toast.success(`Trial extended by ${r.data?.days_added || n} days`);
      // Refresh trial_end on this row
      const trialEnd = r.data?.trial_end;
      setSites(prev => prev.map(x => x.site_id === s.site_id ? { ...x, trial_end: trialEnd, subscription_status: 'trial' } : x));
      // Offer to send "extended" email
      if (window.confirm('Trial extended. Send the owner a confirmation email?')) {
        try {
          await axios.post(`${API}/master/sites/${s.site_id}/send-trial-email`, { variant: 'extended' }, { headers });
          toast.success('Email sent');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Email failed'); }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to extend trial'); }
    finally { setBusy(null); }
  };

  const handleSendTrialEmail = async (s) => {
    const variant = window.confirm(`Send trial-ending email to ${s.owner_email}? Click OK for "ending soon", Cancel to choose "trial has ended" instead.`) ? 'ending' : 'ended';
    setBusy(s.site_id);
    try {
      await axios.post(`${API}/master/sites/${s.site_id}/send-trial-email`, { variant }, { headers });
      toast.success(`Email sent (${variant})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Email failed'); }
    finally { setBusy(null); }
  };

  const stats = {
    total:        sites.length,
    trial:        sites.filter(s => (s.subscription_status || 'trial') === 'trial').length,
    paid:         sites.filter(s => ['active', 'paid'].includes(s.subscription_status)).length,
    published:    sites.filter(s => s.published).length,
    suspended:    sites.filter(s => s.suspended).length,
    onboarded:    sites.filter(s => s.onboarding_completed).length,
  };

  return (
    <div className="space-y-6" data-testid="master-sites-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50">Website Builder</p>
          <h1 className="text-3xl font-bold text-white mt-1" data-testid="sites-title" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>All Sites &amp; Signups</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">Every website created via the builder — trial &amp; monthly. Suspend, delete, or open any site.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by owner, email, or site name…"
            className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/40 w-[320px] focus:outline-none focus:border-[#A78BFA]/50"
            data-testid="sites-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: 'all',       label: 'Total Sites',  value: stats.total,        tone: 'text-white' },
          { key: 'trial',     label: 'Trial',        value: stats.trial,        tone: 'text-yellow-400' },
          { key: 'paid',      label: 'Monthly',      value: stats.paid,         tone: 'text-green-400' },
          { key: 'published', label: 'Published',    value: stats.published,    tone: 'text-blue-300' },
          { key: 'suspended', label: 'Suspended',    value: stats.suspended,    tone: 'text-red-400' },
          { key: 'all',       label: 'Onboarded',    value: stats.onboarded,    tone: 'text-violet-300' },
        ].map((s, i) => (
          <button
            key={`${s.key}-${i}`}
            onClick={() => setFilter(s.key)}
            className={`text-left bg-[#171A2C]/60 backdrop-blur-xl border rounded-2xl px-5 py-4 transition-all hover:border-white/20 ${filter === s.key ? 'border-[#A78BFA]/60 shadow-[0_0_24px_rgba(139,92,246,0.18)]' : 'border-white/5'}`}
            data-testid={`stat-${s.label.toLowerCase()}`}
          >
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50">{s.label}</p>
            <p className={`text-3xl font-bold mt-2 ${s.tone}`} style={{ fontFamily: 'Syne, sans-serif' }}>{s.value}</p>
          </button>
        ))}
      </div>

      {/* Filter strip */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { id: 'all',       label: 'All sites' },
          { id: 'trial',     label: 'Trial only' },
          { id: 'paid',      label: 'Monthly only' },
          { id: 'published', label: 'Published' },
          { id: 'suspended', label: 'Suspended' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 text-[10px] tracking-[0.18em] font-bold uppercase rounded-md transition-all ${filter === f.id ? 'bg-[#A78BFA]/20 text-[#A78BFA]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#A78BFA]" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-white/60">
          <Globe size={32} className="mx-auto text-white/30 mb-3" />
          {sites.length === 0 ? 'No sites yet — once someone signs up via /website-builder, they will appear here.' : 'No sites match the filter.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <SiteRow
              key={s.site_id}
              s={s}
              busy={busy}
              onDelete={handleDelete}
              onHardDelete={handleHardDelete}
              onToggleSuspend={handleToggleSuspend}
              onGrantFounding={handleGrantFounding}
              onRevokeFounding={handleRevokeFounding}
              onExtendTrial={handleExtendTrial}
              onSendTrialEmail={handleSendTrialEmail}
              onEditSite={handleEditSite}
              onRequestAccess={(site) => setRequestDialog({ site, message: '' })}
              onCancelDeletion={handleCancelDeletion}
              onExport={handleExport}
            />
          ))}
        </div>
      )}

      {/* ─── Request Access dialog ──────────────────────────────── */}
      <Dialog open={!!requestDialog} onOpenChange={(o) => !o && setRequestDialog(null)}>
        <DialogContent className="bg-[#15152E] border-white/10 text-white max-w-md" data-testid="request-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-syne flex items-center gap-2">
              <KeyRound size={18} className="text-[#A78BFA]" /> Request edit access
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs">
              Email the owner asking to collaborate as a contributor.
            </DialogDescription>
          </DialogHeader>
          {requestDialog && (
            <div className="space-y-4 text-sm">
              <p className="text-white/70">
                The owner of <strong className="text-white">{requestDialog.site?.business_name || requestDialog.site?.name}</strong>
                {' '}(<span className="font-mono text-xs">{requestDialog.site?.owner_email}</span>) will get an email and an entry in their Approvals inbox. They can Allow or Reject.
              </p>
              <div>
                <Label htmlFor="req-msg" className="text-white/70 text-xs uppercase tracking-wider">Optional message</Label>
                <textarea
                  id="req-msg"
                  rows={4}
                  value={requestDialog.message}
                  onChange={(e) => setRequestDialog({ ...requestDialog, message: e.target.value })}
                  placeholder="Hey! I'd love to help finish your site. Mind if I get edit access?"
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none focus:border-[#A78BFA]/50"
                  data-testid="request-access-message"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setRequestDialog(null)} className="px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
            <button
              onClick={submitRequestAccess}
              disabled={!!busy}
              className="px-5 py-2 rounded-xl text-sm font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white disabled:opacity-50"
              data-testid="request-access-submit"
            >
              Send request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Schedule Deletion dialog ──────────────────────────── */}
      <Dialog open={!!scheduleDialog} onOpenChange={(o) => !o && setScheduleDialog(null)}>
        <DialogContent className="bg-[#15152E] border-red-500/20 text-white max-w-md" data-testid="schedule-deletion-dialog">
          <DialogHeader>
            <DialogTitle className="font-syne flex items-center gap-2 text-red-300">
              <ShieldAlert size={18} /> Schedule site deletion
            </DialogTitle>
            <DialogDescription className="text-white/55 text-xs">
              Suspend the site immediately and hard-delete in N days. Owner gets an email with export options.
            </DialogDescription>
          </DialogHeader>
          {scheduleDialog && (
            <div className="space-y-4 text-sm">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 text-xs text-red-200/85">
                <strong className="text-red-300">{scheduleDialog.site?.business_name || scheduleDialog.site?.name}</strong>
                {' '}(owner: <span className="font-mono">{scheduleDialog.site?.owner_email}</span>) will be suspended immediately (read-only) and hard-deleted in N days. The owner will be emailed with their export options.
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Notice period (1–30 days)</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input
                    type="range" min="1" max="30" step="1"
                    value={scheduleDialog.days}
                    onChange={(e) => setScheduleDialog({ ...scheduleDialog, days: Number(e.target.value) })}
                    className="flex-1 accent-red-400"
                    data-testid="schedule-deletion-days-slider"
                  />
                  <div className="w-16 text-center text-2xl font-bold text-red-300 font-syne tabular-nums">{scheduleDialog.days}</div>
                </div>
                <p className="text-[11px] text-white/40 mt-1">After scheduling you can <strong>extend</strong> but not shorten.</p>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-wider">Reason (shared in the owner&apos;s email)</Label>
                <textarea
                  rows={3}
                  value={scheduleDialog.reason}
                  onChange={(e) => setScheduleDialog({ ...scheduleDialog, reason: e.target.value })}
                  placeholder="e.g., Repeated terms-of-service violations on storefront listings."
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none focus:border-red-400/40"
                  data-testid="schedule-deletion-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <button
              onClick={() => { setScheduleDialog(null); if (scheduleDialog?.site) handleHardDelete(scheduleDialog.site); }}
              className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-red-300/70 hover:text-red-300 hover:underline"
              data-testid="schedule-deletion-skip-notice"
            >
              Skip notice &amp; delete now
            </button>
            <div className="flex gap-2">
              <button onClick={() => setScheduleDialog(null)} className="px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
              <button
                onClick={submitScheduleDeletion}
                disabled={!!busy}
                className="px-5 py-2 rounded-xl text-sm font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-red-500 to-rose-600 text-white disabled:opacity-50"
                data-testid="schedule-deletion-submit"
              >
                Schedule deletion
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SiteRow({ s, busy, onDelete, onHardDelete, onToggleSuspend, onGrantFounding, onRevokeFounding, onExtendTrial, onSendTrialEmail, onEditSite, onRequestAccess, onCancelDeletion, onExport }) {
  const status = s.subscription_status || 'trial';
  const isTrial = status === 'trial';
  const isPaid  = ['active', 'paid'].includes(status);
  const trialEnding = isTrial && typeof s.trial_days_remaining === 'number' && s.trial_days_remaining <= 3;
  const created = s.created_at ? new Date(s.created_at).toLocaleDateString() : '—';
  const domain = s.subdomain ? `${s.subdomain}.entreprenatives.com` : (s.custom_domain || '—');

  // Iter 175i — collab state derived from the master_routes /sites enrichment
  const masterCanEdit = !!s.master_can_edit;
  const pendingReq = s.pending_access_req;
  const deletionSched = s.deletion_scheduled;
  const deletionDaysLeft = deletionSched?.scheduled_for ? Math.max(0, Math.ceil((new Date(deletionSched.scheduled_for) - new Date()) / 86400000)) : null;

  return (
    <div
      className={`bg-[#171A2C]/60 backdrop-blur-xl border rounded-2xl px-5 py-4 flex items-center gap-5 ${s.suspended ? 'border-red-500/40' : 'border-white/5'}`}
      data-testid={`site-row-${s.site_id}`}
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center font-bold text-white shrink-0 text-lg">
        {(s.business_name || s.name || s.owner_name || 'S')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-white truncate text-[15px]" data-testid={`site-name-${s.site_id}`}>
            {s.business_name || s.name || 'Untitled Site'}
          </p>
          {s.is_founding_vendor && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded flex items-center gap-1">
              <Award size={9} /> Founding #{s.founding_vendor_number}
            </span>
          )}
          {isPaid && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-green-400 bg-green-500/10 px-2 py-0.5 rounded flex items-center gap-1">
              <Crown size={9} /> {s.subscription_plan || 'monthly'}
            </span>
          )}
          {isTrial && (
            <span className={`text-[10px] tracking-[0.18em] font-bold uppercase ${trialEnding ? 'text-red-400 bg-red-500/10' : 'text-yellow-400 bg-yellow-500/10'} px-2 py-0.5 rounded flex items-center gap-1`}>
              <Clock size={9} /> Trial · {s.trial_days_remaining ?? '?'}d left
            </span>
          )}
          {s.template_id && <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA] bg-[#A78BFA]/10 px-2 py-0.5 rounded">{s.template_id}</span>}
          {s.published
            ? <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded">Published</span>
            : <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/40 bg-white/5 px-2 py-0.5 rounded">Draft</span>}
          {s.suspended && <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-400 bg-red-500/10 px-2 py-0.5 rounded">Suspended</span>}
          {!s.onboarding_completed && <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/40 bg-white/5 px-2 py-0.5 rounded">In Onboarding</span>}
          {trialEnding && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-400 bg-red-500/15 px-2 py-0.5 rounded flex items-center gap-1">
              <AlertTriangle size={9} /> Expiring
            </span>
          )}
          {pendingReq && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-amber-300 bg-amber-500/10 border border-amber-400/25 px-2 py-0.5 rounded flex items-center gap-1" title={`Pending since ${new Date(pendingReq.created_at).toLocaleString()}`}>
              <Clock size={9} /> Access pending
            </span>
          )}
          {masterCanEdit && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-400/25 px-2 py-0.5 rounded flex items-center gap-1" title="Master is an active contributor on this site">
              <CheckCircle2 size={9} /> Can edit
            </span>
          )}
          {deletionSched && (
            <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-300 bg-red-500/10 border border-red-400/30 px-2 py-0.5 rounded flex items-center gap-1" title={`Hard delete on ${new Date(deletionSched.scheduled_for).toLocaleString()}`}>
              <CalendarX size={9} /> Deleting in {deletionDaysLeft}d
            </span>
          )}
        </div>
        <div className="text-xs text-white/70 mt-2 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5"><Mail size={11} className="text-[#A78BFA]" /> <span className="font-mono text-white/90" data-testid={`site-email-${s.site_id}`}>{s.owner_email || 'no-email'}</span></span>
          {s.owner_name && <span className="text-white/55">· {s.owner_name}</span>}
        </div>
        <p className="text-[11px] text-white/45 mt-1.5">
          {domain} · {s.block_count || 0} blocks · created {created}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {s.subdomain && (
          <>
            <button
              onClick={() => {
                const url = publicUrl(`/site/${s.subdomain}`);
                navigator.clipboard.writeText(url);
                toast.success(`Share link copied: ${url}`);
              }}
              className="p-2 text-[#A78BFA] hover:text-white hover:bg-[#8B5CF6]/10 rounded transition-all"
              title={`Copy live preview URL (${s.subdomain}.entreprenatives.com)`}
              data-testid={`site-copy-preview-${s.site_id}`}
            >
              <Link2 size={15} />
            </button>
            <a
              href={`/site/${s.subdomain}`}
              target="_blank"
              rel="noreferrer"
              className="p-2 text-white/60 hover:text-white"
              title="Open public site"
              data-testid={`site-open-${s.site_id}`}
            >
              <ExternalLink size={15} />
            </a>
          </>
        )}
        {/* Iter 175i — Edit Site (master is a contributor) OR Request Access (client-owned, not yet a contributor) */}
        {masterCanEdit ? (
          <button
            onClick={() => onEditSite(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-300 hover:bg-emerald-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title="Open this site in the website builder"
            data-testid={`site-edit-${s.site_id}`}
          >
            <Edit3 size={11} /> Edit Site
          </button>
        ) : !pendingReq ? (
          <button
            onClick={() => onRequestAccess(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-300 hover:bg-amber-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title="Email the owner asking for edit access"
            data-testid={`site-request-access-${s.site_id}`}
          >
            <KeyRound size={11} /> Request Access
          </button>
        ) : (
          <span className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-300/70 flex items-center gap-1.5" title="Waiting for owner to allow or reject">
            <Clock size={11} /> Awaiting reply
          </span>
        )}
        <button
          onClick={() => onExport(s, 'zip')}
          disabled={busy === s.site_id}
          className="p-2 text-cyan-300 hover:bg-cyan-500/10 rounded transition-all disabled:opacity-40"
          title="Export this site's data (ZIP)"
          data-testid={`site-export-${s.site_id}`}
        >
          <Download size={15} />
        </button>
        {!s.is_founding_vendor ? (
          <button
            onClick={() => onGrantFounding(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-300 hover:bg-amber-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title="Promote to Founding Vendor (e.g. webhook missed)"
            data-testid={`site-grant-founding-${s.site_id}`}
          >
            <Award size={11} /> Make Founding
          </button>
        ) : (
          <button
            onClick={() => onRevokeFounding(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-300/70 hover:text-amber-400 hover:bg-amber-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title={`Revoke Founding #${s.founding_vendor_number}`}
            data-testid={`site-revoke-founding-${s.site_id}`}
          >
            <Award size={11} /> Revoke
          </button>
        )}
        {isTrial && (
          <>
            <button
              onClick={() => onExtendTrial(s)}
              disabled={busy === s.site_id}
              className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-blue-300 hover:bg-blue-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
              title="Extend trial period"
              data-testid={`site-extend-trial-${s.site_id}`}
            >
              <CalendarPlus size={11} /> Extend
            </button>
            <button
              onClick={() => onSendTrialEmail(s)}
              disabled={busy === s.site_id}
              className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-violet-300 hover:bg-violet-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
              title="Send trial-ending email"
              data-testid={`site-send-trial-email-${s.site_id}`}
            >
              <MailCheck size={11} /> Email
            </button>
          </>
        )}
        <button
          onClick={() => onToggleSuspend(s)}
          disabled={busy === s.site_id}
          className={`px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold rounded transition-all flex items-center gap-1.5 ${s.suspended ? 'text-green-400 hover:bg-green-500/10' : 'text-yellow-400 hover:bg-yellow-500/10'} disabled:opacity-40`}
          data-testid={`site-suspend-${s.site_id}`}
        >
          {s.suspended ? <><Play size={11} /> Reopen</> : <><Pause size={11} /> Suspend</>}
        </button>
        {deletionSched ? (
          <button
            onClick={() => onCancelDeletion(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-300 hover:bg-emerald-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title="Cancel the scheduled deletion"
            data-testid={`site-cancel-deletion-${s.site_id}`}
          >
            <CheckCircle2 size={11} /> Cancel deletion
          </button>
        ) : (
          <button
            onClick={() => onDelete(s)}
            disabled={busy === s.site_id}
            className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-red-400 hover:bg-red-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40"
            title="Schedule site for deletion with notice"
            data-testid={`site-delete-${s.site_id}`}
          >
            <CalendarX size={11} /> Schedule Deletion
          </button>
        )}
      </div>
    </div>
  );
}
